import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  useEffect,
  useMemo,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, ButtonGroup } from "reactstrap";
import Select from "react-select";
import "./userPreferences.css";
import { USER, USER_PREFERENCES } from "../../_constants/actionConstants";
import authenticationActions from "../../_actions/authenticationActions";
import userActions from "../../_actions/userActions";
import AlertBox from "../_common/AlertBox";
import EditUserSignatureModal from "./EditUserSignatureModal";
import AddUserModalV2 from "../AdminTasks/AdminUsers/AddUserModalV2";
import EditUserDetailsModal from "../AdminTasks/AdminUsers/EditUserDetailsModal";

const UserPreferencesTabContext = createContext({
  tabId: null,
});

const UserPreferencesMenuContext = createContext({
  selectedSectionId: null,
  setSelectedSectionId: () => {},
  sections: null,
});

export default function UserPreferencesV2(props) {
  const { tabId } = props;

  const sections = useRef({
    display: {
      sectionId: "display",
      sectionTitle: "Display Settings",
      icon: "tv",
      isDisabled: true,
      render: () => <UserPreferences_Display />,
    },
    userProfile: {
      sectionId: "userProfile",
      sectionTitle: "User Profile",
      icon: "user-cog",
      isDisabled: false,
      render: () => <UserPreferences_UserProfile />,
    },
    notifications: {
      sectionId: "notifications",
      sectionTitle: "Notifications",
      icon: "bell",
      isDisabled: true,
      render: () => <UserPreferences_Body />,
    },
    projects: {
      sectionId: "projects",
      sectionTitle: "Projects",
      icon: "project-diagram",
      isDisabled: true,
      render: () => <UserPreferences_Body />,
    },
    dailyReports: {
      sectionId: "dailyReports",
      sectionTitle: "Daily Reports",
      icon: "file-alt",
      isDisabled: true,
      render: () => <UserPreferences_Body />,
    },
  });

  const [selectedSectionId, setSelectedSectionId] = useState("userProfile");

  return (
    <UserPreferencesTabContext.Provider value={{ tabId }}>
      <div className="row m-2">
        <div className="col-12 px-2">
          <h3 className="text-muted">User Preferences</h3>
          <hr className="my-2" />
          <div className="d-flex flex-row w-100">
            <UserPreferencesMenuContext.Provider
              value={{
                selectedSectionId,
                setSelectedSectionId,
                sections: sections.current,
              }}
            >
              <div className="flex-column pr-3 border-right userPref__submenu">
                <UserPreferencesMenu />
              </div>
              <div className="flex column pl-3 w-100">
                {selectedSectionId ? (
                  <>
                    <h4 className="text-muted">
                      {sections.current[selectedSectionId]?.sectionTitle ?? ""}
                    </h4>
                    {sections.current[selectedSectionId]?.render()}
                  </>
                ) : (
                  ""
                )}
              </div>
            </UserPreferencesMenuContext.Provider>
          </div>
        </div>
      </div>
    </UserPreferencesTabContext.Provider>
  );
}

function UserPreferencesMenu() {
  const userPrefMenuContext = useContext(UserPreferencesMenuContext);
  return (
    <ul className="list-group pointer-cursor">
      {(Object.values(userPrefMenuContext.sections) || []).map((s) => (
        <UserPreferencesMenuItem
          key={`userPrefMenuItemKey_${s.sectionId}`}
          sectionId={s.sectionId}
          sectionTitle={s.sectionTitle}
          icon={s.icon}
          isDisabled={s.isDisabled}
        />
      ))}
    </ul>
  );
}

function UserPreferencesMenuItem(props) {
  const { sectionId, sectionTitle, icon, isDisabled } = props;

  const userPrefMenuContext = useContext(UserPreferencesMenuContext);

  return (
    <li
      className={`list-group-item userPref__submenu--item${
        isDisabled ? " disabled" : ""
      }${userPrefMenuContext.selectedSectionId === sectionId ? " active" : ""}`}
      onClick={() => !isDisabled && userPrefMenuContext.setSelectedSectionId(sectionId)}
    >
      <FontAwesomeIcon icon={icon} className="fa-fw mr-2" />
      {sectionTitle}
    </li>
  );
}

function UserPreferences_Body() {
  const userPrefMenuContext = useContext(UserPreferencesMenuContext);

  return (
    <>
      <h4>
        {userPrefMenuContext.sections[userPrefMenuContext.selectedSectionId]
          ?.sectionTitle ?? ""}
      </h4>
    </>
  );
}

function UserPreferences_Display() {
  const userPrefMenuContext = useContext(UserPreferencesMenuContext);
  const { selectedSectionId, sections } = userPrefMenuContext;
  const dispatch = useDispatch();
  const userPreferences = useSelector(
    (state) => state.authentication.user.userPreferences || null
  );

  const fontSizeOptions = useRef([
    { value: 1, label: "Small" },
    { value: 2, label: "Medium" },
    { value: 3, label: "Large" },
  ]);

  const update = useCallback(
    (configId, value) => {
      dispatch({
        type: USER_PREFERENCES.UPDATE,
        sectionId: selectedSectionId,
        configId,
        configValue: value,
      });
      dispatch(authenticationActions.saveUserPrefs());
    },
    [selectedSectionId]
  );

  return (
    <div>
      <ConfigButtonGroup
        update={(value) => update("isDarkMode", value === "dark")}
        value={
          userPreferences
            ? userPreferences[selectedSectionId].isDarkMode
              ? "dark"
              : "light"
            : "light"
        }
        label="Display Mode:"
        options={[
          { id: "light", label: "Light" },
          { id: "dark", label: "Dark" },
        ]}
      />
      <ConfigDropdown
        id={`${selectedSectionId}_fontSize`}
        label="Font Size:"
        className="w-50"
        update={(value) => update("fontSize", value)}
        value={userPreferences ? userPreferences[selectedSectionId].fontSize : 2}
        options={fontSizeOptions.current}
      />
    </div>
  );
}

function UserPreferences_UserProfile() {
  const userPrefMenuContext = useContext(UserPreferencesMenuContext);
  const { selectedSectionId, sections } = userPrefMenuContext;

  return (
    <div>
      <UserInfoSetting />
      <UserProfilePictureSetting />
      <UserSignatureSetting />
    </div>
  );
}

function UserInfoSetting(props) {
  const {} = props;

  const userPrefTabContext = useContext(UserPreferencesTabContext);

  const dispatch = useDispatch();

  const user = useSelector((state) => state.authentication.user || null);
  const userRoles = useSelector((state) => state.users.userRoles || null);

  useEffect(() => dispatch(userActions.getUserRoles()), []);

  const userRole = useMemo(
    () => (userRoles || []).find((ur) => ur.userRoleId === user?.userRoleId ?? 0),
    [userRoles, user]
  );

  const [showModal_editUser, setShowModal_editUser] = useState(false);
  const toggleModal_editUser = () => setShowModal_editUser(!showModal_editUser);

  return (
    <>
      <div className="form-row form-group mb-0 py-1 border-top">
        <label className="col-sm-2 col-form-label">User Info:</label>
        <div className="col-sm-10 card my-2">
          <div className="card-body p-2">
            <div className="d-flex flex-row mb-2">
              <button
                id="editUserInfo"
                name="editUserInfo"
                className="btn btn-sm btn-primary"
                onClick={toggleModal_editUser}
              >
                Edit...
              </button>
            </div>
            <div className="d-flex flex-row border-top">
              <div className="w-25">Name:</div>
              <span>{user?.fullName}</span>
            </div>
            <div className="d-flex flex-row border-top">
              <div className="w-25">Employee No:</div>
              <span>{user?.employeeNo}</span>
            </div>
            <div className="d-flex flex-row border-top">
              <div className="w-25">Unit:</div>
              <span>{user?.unit.distUnit}</span>
            </div>
            <div className="d-flex flex-row border-top">
              <div className="w-25">Email:</div>
              <span>{user?.email ?? "(N/A)"}</span>
            </div>
            <div className="d-flex flex-row border-top">
              <div className="w-25">Phone:</div>
              <span>{user?.phoneNumber ?? "(N/A)"}</span>
            </div>
            <div className="d-flex flex-row border-top">
              <div className="w-25">Classification:</div>
              <span>{userRole?.description}</span>
            </div>
          </div>
        </div>
      </div>
      {showModal_editUser && user && (
        <EditUserDetailsModal
          showModal={showModal_editUser}
          toggleModal={toggleModal_editUser}
          tabId={userPrefTabContext.tabId}
          user={user}
          isNewUserRegistration={false}
          allowRegisterAsOfficeAdmin={false}
        />
      )}
    </>
  );
}

function UserProfilePictureSetting(props) {
  const {} = props;

  const dispatch = useDispatch();
  const userPrefTabContext = useContext(UserPreferencesTabContext);

  const user = useSelector((state) => state.authentication.user || null);
  const profilePicDataUrl = useSelector(
    (state) =>
      state.users.miscSessionData[userPrefTabContext.tabId]?.profilePicDataUrl || null
  );
  const profilePicFilename = useSelector(
    (state) =>
      state.users.miscSessionData[userPrefTabContext.tabId]?.profilePicFilename || null
  );

  useEffect(() => {
    const getProfPic = async (userId) => {
      return await dispatch(userActions.getProfilePic(userPrefTabContext.tabId, userId));
    };
    if (user) getProfPic(user.userId);
  }, [user]);

  const handleChange_fileInput = async (e) => {
    const files = Array.from(e.target.files);
    const _file = (files || []).length ? files[0] : null;
    if (_file) {
      const formData = new FormData();

      formData.append("user_profile_picture", _file);
      formData.append("userId", user?.userId);

      await dispatch(
        userActions.uploadProfilePic(userPrefTabContext.tabId, user.userId, formData)
      );
    }
  };

  return (
    <div className="form-row form-group mb-0 py-1 border-top">
      <div className="col-sm-2">
        <label className="col-form-label">Profile Picture:</label>
      </div>
      <div className="col-sm-10 card my-2">
        <div className="card-body p-2">
          <div className="d-flex flex-row">
            <input
              type="file"
              id="upload_custom_form"
              onChange={handleChange_fileInput}
              accept=".jpg, .jpeg, .png, .gif"
              hidden
              disabled={!user}
            />
            <label
              htmlFor="upload_custom_form"
              className="btn btn-sm btn-primary mb-0 mr-2"
            >
              <FontAwesomeIcon icon="upload" className="fa-fw mr-1" />
              Upload...
            </label>
            {profilePicFilename ? (
              <div className="d-flex flex-row">
                <button
                  id="deleteProfilePic"
                  name="deleteProfilePic"
                  className="btn btn-sm btn-danger"
                  onClick={() =>
                    dispatch(
                      userActions.deleteProfilePic(userPrefTabContext.tabId, user.userId)
                    )
                  }
                >
                  Delete
                </button>
              </div>
            ) : (
              <span className="my-auto" style={{ color: "red" }}>
                (No File Uploaded)
              </span>
            )}
          </div>
          {profilePicDataUrl && (
            <div className="mt-2">
              <img
                src={profilePicDataUrl}
                alt="(User Profile Picture)"
                className="border"
                style={{ maxWidth: "250px", maxHeight: "200px" }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function UserSignatureSetting(props) {
  const {} = props;

  const userPrefMenuContext = useContext(UserPreferencesMenuContext);
  const { selectedSectionId } = userPrefMenuContext;

  const userPrefTabContext = useContext(UserPreferencesTabContext);

  const dispatch = useDispatch();
  const user = useSelector((state) => state.authentication.user || null);
  const fetchingUser = useSelector((state) => state.users.fetching);
  const userPreferences = useSelector(
    (state) => state.authentication.user.userPreferences || null
  );

  const existSignatureData = useSelector(
    (state) => state.users.miscSessionData.userSignature || null
  );

  const [showModal_editSignature, setShowModal_editSignature] = useState(false);
  const toggleModal_editSignature = () =>
    setShowModal_editSignature(!showModal_editSignature);

  const update = useCallback(
    (value) => {
      dispatch({
        type: USER_PREFERENCES.UPDATE,
        sectionId: selectedSectionId,
        configId: "doNotPromptForSignature",
        configValue: value,
      });
      dispatch(authenticationActions.saveUserPrefs());
    },
    [selectedSectionId]
  );

  // Load existing signature when initializing.
  // Signature could have been created in User Preferences module.
  useEffect(() => {
    const getUserSig = async (userId) => {
      const suppressWarning = true;
      await dispatch(userActions.getSignature(userId, suppressWarning));
    };
    if (user) getUserSig(user.userId);
  }, [user]);

  return (
    <>
      <div className="form-row form-group mb-0 py-1 border-top">
        <div className="col-sm-2">
          <label className="col-form-label">Signature:</label>
        </div>
        <div className="col-sm-10 card my-2">
          <div className="card-body p-2">
            <div className="d-flex flex-row mb-2">
              <button
                id="editSignature"
                name="editSignature"
                className="btn btn-sm btn-primary"
                onClick={toggleModal_editSignature}
                disabled={fetchingUser}
              >
                Create/Change Signature...
              </button>
            </div>
            <div className="">
              <div className="mb-2">
                <div className="d-flex flex-row">Stored Signature:</div>
                <div
                  className="p-2 d-flex flex-row mb-0"
                  style={{
                    height: "100px",
                    width: "fit-content",
                    border: "0.05rem solid lightgray",
                    borderRadius: "3px",
                  }}
                >
                  {!fetchingUser ? (
                    existSignatureData?.dataUrl ? (
                      <img
                        src={existSignatureData.dataUrl}
                        alt="Author Signature"
                        style={{ width: "100%", height: "100%" }}
                      />
                    ) : (
                      "(No Signature)"
                    )
                  ) : (
                    "Loading..."
                  )}
                </div>
                {existSignatureData?.createdOn && (
                  <div className="d-flex flex-row text-small">
                    Created On {new Date(existSignatureData.createdOn).toLocaleString()}
                  </div>
                )}
              </div>
              <ConfigCheck
                id="skipSignaturePrompt"
                label="Use this signature to sign documents, and do not prompt me for signatures."
                value={
                  userPreferences
                    ? userPreferences[selectedSectionId]?.doNotPromptForSignature ?? false
                    : false
                }
                update={(value) => update(value)}
                isDisabled={!existSignatureData}
              />
              <AlertBox
                color="info"
                icon="info-circle"
                title="Electronic Signature Disclosure"
              >
                When you submit or update documents with your signature, you are verifying
                that the statements and information provided are true and correct, and
                attest to the validity of all contents within this electronic submission,
                to the best of your knowledge.
              </AlertBox>
            </div>
          </div>
        </div>
      </div>
      {showModal_editSignature && (
        <EditUserSignatureModal
          tabId={userPrefTabContext.tabId}
          userId={user.userId}
          showModal={showModal_editSignature}
          toggleModal={toggleModal_editSignature}
        />
      )}
    </>
  );
}

function ConfigButtonGroup(props) {
  const { label, value, options, update } = props;

  const handleClick = (e) => {
    e.preventDefault();
    update(e.target.name);
  };

  return (
    <div className="form-row form-group mb-0 py-1 border-top">
      <label className="col-sm-2 col-form-label">{label}</label>
      <div className="col-sm-10">
        <ButtonGroup>
          {options.map((o, index) => (
            <Button
              key={`userPrefConfig_${o.id}_${index}`}
              name={o.id}
              id={`userPrefConfig_${o.id}_${index}`}
              color="primary"
              outline
              onClick={handleClick}
              active={value === o.id}
            >
              {value === o.id ? <b>{o.label}</b> : o.label}
            </Button>
          ))}
        </ButtonGroup>
      </div>
    </div>
  );
}

function ConfigCheck(props) {
  const { id, label, value, update, isDisabled } = props;
  const [_value, _setValue] = useState(value ?? false);

  useEffect(() => _setValue(value), [value]);

  const handleChange = (e) => {
    _setValue(e.target.checked);
    update(e.target.checked);
  };

  return (
    <div className="form-row form-group mb-0 py-1 border-top">
      <div className="custom-control-inline custom-control custom-checkbox">
        <input
          type="checkbox"
          name={id}
          id={id}
          className="custom-control-input my-auto"
          value={_value}
          checked={_value}
          onChange={handleChange}
          disabled={isDisabled}
        />
        <label htmlFor={id} className="custom-control-label my-auto">
          {label}
        </label>
      </div>
    </div>
  );
}

function ConfigDropdown(props) {
  const { id, label, className, value, options, update } = props;
  const [selectedOption, setSelectedOption] = useState(
    value ? (options || []).find((o) => o.value === value) || null : null
  );
  const handleChange = (selectedOption) => {
    setSelectedOption(selectedOption);
    update(selectedOption.value);
  };

  return (
    <div className="form-row form-group mb-0 py-1 border-top">
      <label className="col-sm-2 col-form-label">{label}</label>
      <div className="col-sm-10">
        <Select
          name={id}
          id={id}
          className={className}
          value={selectedOption}
          options={options}
          onChange={handleChange}
          styles={{
            // Fixes the overlapping problem of the component
            menu: (provided) => ({ ...provided, zIndex: 9999 }),
          }}
        />
      </div>
    </div>
  );
}

function ConfigInput(props) {
  const { id, label, value, update } = props;
  const [_value, _setValue] = useState(value);

  useEffect(() => _setValue(value), [value]);

  const handleChange = (e) => {
    _setValue(e.target.value);
    update(e.target.value);
  };

  return (
    <div className="form-row form-group mb-0 py-1 border-top">
      <label htmlFor={id} className="col-sm-2 col-form-label">
        {label}
      </label>
      <input
        name={id}
        id={id}
        className="form-control col-sm-10 my-auto"
        value={_value}
        onChange={handleChange}
      />
    </div>
  );
}
